/**
 * Homepage copy, adapted from `Take B - Bluebird.dc.html`.
 *
 * Step 4 of the handoff is a ruling: port the comp's strings exactly, including
 * ones that look wrong, and let Aaron do the copy pass afterward against
 * something he can see. Text length drives wrapping, wrapping drives block
 * height, and block height drives every spacing measurement below it — so
 * editing copy during a pixel-exact port is the fastest way to lose the port.
 *
 * The 2026-08-18 copy pass removed hardcoded inventory counts and unsupported
 * claims, then tightened the language against Ryan's recorded shop notes.
 *
 * See `shop.ts` for the one hard limit on editing: rewording is free, asserting
 * a new fact is not.
 */

export const home = {
  hero: {
    /** The fixed clause. The destination after it rotates. */
    headlinePrefix: "The quickest way to",
    body:
      "Family-owned in Boulder and focused on electric cargo bikes. Bring the kids, compare bikes in person, and take a free 30-minute test ride before you decide.",
    photoNote:
      "background photo — a parent and children using a family cargo bike",
    primary: "Schedule a test ride",
    secondary: "See the bikes",
  },

  /**
   * The rotating destinations, in the comp's order.
   *
   * Aaron approved this hero specifically, overruling an earlier draft that
   * called "quickest" an unverifiable speed claim. Two structural properties
   * carry the line and must survive any copy pass: **grammatical variation**
   * (some entries take "the", some are bare proper nouns) and **a deliberate
   * pattern-break as the last entry** ("literally anywhere"). Drop either and
   * the line flattens. NCAR is a joke — it is a brutal climb and locals get it.
   *
   * Every entry is a real, named, public place, which is what keeps the line
   * free of invention risk. Ryan has not yet vetted which of these read as
   * local versus as a tourist's list.
   */
  destinations: [
    "Chautauqua",
    "the Boulder Farmers Market",
    "school drop-off",
    "Valmont Bike Park",
    "Pearl Street",
    "Scott Carpenter Pool",
    "the Sanitas trailhead",
    "soccer at Stazio",
    "Eben G. Fine Park",
    "Gunbarrel",
    "NCAR",
    "literally anywhere",
  ],

  /** How fast the destination advances, in ms. The comp's default. */
  rotationMs: 2400,

  /**
   * The promise strip.
   *
   * Reshaped 2026-08-19 after Aaron picked the tinted-band direction off the
   * options canvas (docs/design/promise-strip/). Two things moved and nothing
   * new was asserted:
   *
   *   - "Cargo bikes are the whole shop." was the third sentence of a
   *     four-sentence centered paragraph. It is the shop's sharpest line and
   *     nobody reads the third sentence of a centered paragraph, so it is the
   *     section's heading now.
   *   - The eyebrow leads with the place. "Boulder" was the last word of a
   *     12px mono line; it is the thing a visitor is actually checking for.
   *
   * `lede` must keep leading with the two heritage claims in that order —
   * script/copy_identity_check.mjs asserts the rendered text starts with them,
   * and the scope split is deliberate: Colorado for cargo bikes, Boulder County
   * for e-bikes. The address and hours under the cards are NOT repeated here;
   * they render from `shop` so there is one copy of each on the site.
   */
  promise: {
    eyebrow: "Family-owned in Boulder, CO",
    heading: "Find the cargo bike that fits your family",
    lede:
      "We help families compare bikes for school drop-off, groceries, and everyday trips. See what's on the floor, ask direct questions, and ride before you choose.",
    points: [
      {
        title: "Compare the right types",
        body: "See front-loaders, longtails, and compact cargo bikes. We'll narrow the options by passengers, storage, and the routes you ride.",
      },
      {
        title: "Bring the family",
        body: "Load the kids or the gear you carry most. A free 30-minute test ride will tell you more than a spec sheet.",
      },
      {
        title: "Get support after the sale",
        body: "Bring the bike back here for inspections, tunes, and repairs from a shop focused on cargo bikes.",
      },
    ],
  },

  bikes: {
    heading: "The bikes",
    seeAll: "See all bikes →",
    brands: "The brands on the floor — Tern · Urban Arrow · Xtracycle · Riese & Müller · Benno · Gazelle · Flyer",
    brandBoardLabel: "The brands on the floor",
    /**
     * The seven brands, with Tern and Urban Arrow first.
     *
     * These are the SAME six as BikesController::CATALOG_BRANDS and as the
     * `brands` line above. Yuba is not among them — the shop does not carry it,
     * which a stale logo file in public/images/brands/ still claims. Changing
     * this list means changing all three.
     */
    brandList: [
      { slug: "tern", name: "Tern" },
      { slug: "urban-arrow", name: "Urban Arrow" },
      { slug: "xtracycle", name: "Xtracycle" },
      { slug: "riese-muller", name: "Riese & Müller" },
      { slug: "benno", name: "Benno" },
      { slug: "gazelle", name: "Gazelle" },
      { slug: "flyer", name: "Flyer" },
    ],
    /** The line under a card whose bike is gone. */
    soldPrefix: "Availability not confirmed — ",
    soldLink: "ask about this bike",
  },

  savings: {
    heading: "See which Colorado savings apply to you",
    body:
      "Colorado residents can use a $225 point-of-sale credit. A separate state lottery may provide income-qualified riders as much as $1,300 for a cargo bike.",
    cta: "Compare the savings",
    rows: [
      {
        figure: "$225",
        countUp: 225,
        title: "Point-of-sale tax credit",
        body: "No income test. One credit per Colorado resident each calendar year.",
      },
      {
        figure: "$1,300",
        countUp: 1300,
        title: "Income-qualified rebate",
        body:
          "Qualified applicants may receive $1,000 plus a $300 cargo-bike bonus through a state lottery.",
      },
    ],
  },

  shopBand: {
    photoNote: "photo — the roll-up door open to the street, bikes visible inside",
    heading: "Family cargo bikes are our focus",
    body1:
      "We help Boulder families compare bikes for carrying kids, groceries, and everyday gear. Bring the people and things you plan to carry so you can test the fit together.",
    body2:
      "Already bought your bike here? Schedule a $45 safety inspection, a tune starting at $180, or a repair visit.",
    primary: "Service & repairs",
    secondary: "More about the shop →",
  },

  testimonials: {
    eyebrow: "Google reviews",
    heading: "What local families say",
  },
} as const;

/**
 * The six bikes the comp hardcodes into its grid.
 *
 * These are NOT shipped to visitors. They exist so `script/design_diff.mjs` can
 * diff the grid's layout against the comp: the comp renders six placeholder
 * cards, a local dev box with no Shopify credentials renders two fixtures, and
 * production renders the real catalog. Without a fixed set on both sides the
 * grid could only be excluded from the diff, and an excluded section is a
 * section nobody is checking.
 *
 * Served only in development, only when `?__designfixture=1` is present. See
 * PagesController#home.
 */
export const DESIGN_FIXTURE_BIKES = [
  { name: "Urban Arrow Family", price: "$7,999", shot: "photo — side profile, Urban Arrow Family", shotInUse: "photo — same bike, kids climbing into the box", available: true, sold: false },
  { name: "Tern GSD R14", price: "$9,499", shot: "photo — side profile, Tern GSD R14", shotInUse: "photo — same bike, loaded with groceries", available: true, sold: false },
  { name: "Riese & Müller Load4 75", price: "$14,858", shot: "photo — side profile, R&M Load4 75", shotInUse: "photo — same bike, riding the creek path", available: true, sold: false },
  { name: "Tern HSD P5i", price: "$3,999", shot: "photo — side profile, Tern HSD P5i", shotInUse: "photo — same bike, parked at a school rack", available: true, sold: false },
  { name: "Benno 46er", price: "$5,299", shot: "photo — side profile, Benno 46er", shotInUse: "photo — same bike, by the roll-up door", available: false, sold: true },
  { name: "Benno Boost E 10D", price: "$4,599", shot: "photo — side profile, Benno Boost E", shotInUse: "photo — same bike, panniers full", available: true, sold: false },
] as const;
